import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Typography, Stack, FormControl, InputLabel, Select, MenuItem, Alert,
  CircularProgress, Chip,
} from '@mui/material';
import { Warning, CheckCircle } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { doRaiseEscalation, doResolveEscalation } from '../../../reduxcomponents/slices/tripManagementSlice';

// ── Raise Escalation ─────────────────────────────────────────────────────────
export const RaiseEscalationDialog = ({ open, onClose, tripId, onSuccess }) => {
  const dispatch = useDispatch();
  const { loading } = useSelector(s => s.tripManagement);
  const [form, setForm] = useState({ description: '', severity: 'Medium' });
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!form.description.trim()) { setError('Description is required.'); return; }
    try {
      await dispatch(doRaiseEscalation({ id: tripId, data: form })).unwrap();
      onSuccess?.();
      onClose();
      setForm({ description: '', severity: 'Medium' });
    } catch (err) {
      setError(err || 'Failed to raise escalation.');
    }
  };

  const SEVERITY_COLORS = { Low: 'info', Medium: 'warning', High: 'error', Critical: 'error' };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <Warning color="error" />
          <Typography variant="h6">Raise Escalation</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Stack spacing={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Severity</InputLabel>
            <Select
              value={form.severity}
              label="Severity"
              onChange={e => setForm(f => ({ ...f, severity: e.target.value }))}
            >
              {['Low', 'Medium', 'High', 'Critical'].map(s => (
                <MenuItem key={s} value={s}>
                  <Chip label={s} color={SEVERITY_COLORS[s]} size="small" />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Description"
            multiline
            rows={4}
            fullWidth
            size="small"
            value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            placeholder="Describe the issue in detail..."
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleSubmit}
          disabled={loading.action}
          startIcon={loading.action ? <CircularProgress size={16} /> : <Warning />}
        >
          Raise Escalation
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Resolve Escalation ────────────────────────────────────────────────────────
export const ResolveEscalationDialog = ({ open, onClose, tripId, escalationId, onSuccess }) => {
  const dispatch = useDispatch();
  const { loading } = useSelector(s => s.tripManagement);
  const [resolution, setResolution] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!resolution.trim()) { setError('Resolution is required.'); return; }
    try {
      await dispatch(doResolveEscalation({ id: tripId, escId: escalationId, data: { resolution } })).unwrap();
      onSuccess?.();
      onClose();
      setResolution('');
    } catch (err) {
      setError(err || 'Failed to resolve escalation.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <CheckCircle color="success" />
          <Typography variant="h6">Resolve Escalation</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField
          label="Resolution"
          multiline
          rows={4}
          fullWidth
          size="small"
          value={resolution}
          onChange={e => setResolution(e.target.value)}
          placeholder="Describe how this was resolved..."
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleSubmit}
          disabled={loading.action}
          startIcon={loading.action ? <CircularProgress size={16} /> : <CheckCircle />}
        >
          Mark Resolved
        </Button>
      </DialogActions>
    </Dialog>
  );
};
